import React, { useMemo, useState } from "react";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  withGoogleMap
} from "react-google-maps";
import { useNavigate } from "react-router";
import markerIcon from "../../../images/marker.png";
import { Event } from "../../../utils/entities/Event";

interface EventGroup {
  key: string;
  events: Event[];
  lat: number;
  lng: number;
}

// Agrupa oportunidades que comparten la misma ONG y coordenadas (misma sede).
// Sin esto, Google Maps apila los <Marker> en el mismo pixel y solo queda
// visible/clickeable el de arriba, ocultando el resto.
function groupEvents(events: Event[]): EventGroup[] {
  const map = new Map<string, Event[]>();
  events.forEach((e) => {
    // Redondeo a ~1m (5 decimales) para agrupar coords esencialmente idénticas
    const lat = Number(e.coords.x.toFixed(5));
    const lng = Number(e.coords.y.toFixed(5));
    // Clave por ongId + coords: ONGs distintas en la misma dirección
    // siguen siendo pines separados.
    const key = `${e.ongId}|${lat}|${lng}`;
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(e);
    } else {
      map.set(key, [e]);
    }
  });
  const groups: EventGroup[] = [];
  map.forEach((evts, key) => {
    groups.push({
      key,
      events: evts,
      lat: Number(evts[0].coords.x.toFixed(5)),
      lng: Number(evts[0].coords.y.toFixed(5)),
    });
  });
  return groups;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minWidth: 280,
    maxWidth: 380,
    fontFamily: "inherit",
  },
  header: {
    borderBottom: "1px solid #eee",
    paddingBottom: 8,
    marginBottom: 8,
  },
  ongName: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1d1d1f",
  },
  count: {
    fontSize: 12,
    color: "#6e6e73",
    marginTop: 3,
  },
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
    maxHeight: 420,
    overflowY: "auto" as const,
  },
  item: {
    padding: "8px 0",
    borderBottom: "1px solid #f2f2f2",
  },
  itemTitulo: {
    fontWeight: 600,
    fontSize: 14,
    color: "#1d1d1f",
    marginBottom: 5,
  },
  itemDesc: {
    fontSize: 12,
    color: "#6e6e73",
    marginBottom: 8,
    lineHeight: 1.4,
  },
  button: {
    border: "none",
    background: "#725153",
    color: "#fff",
    padding: "5px 14px",
    borderRadius: 4,
    fontSize: 12,
    cursor: "pointer",
  },
};

const NormalMap =
  withGoogleMap((props: any) => {
    const events: Event[] = props.events;
    const navigate = useNavigate();
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    const groups = useMemo(() => groupEvents(events || []), [events]);

    return (
      <GoogleMap
        defaultOptions={{
          center: {
            lat: -34.8833,
            lng: -56.1667,
          },
          gestureHandling: "greedy",
          disableDefaultUI: true,
          zoom: 12,
          minZoom: 8,
          maxZoom: 15,
          streetViewControl: false,
          rotateControl: false,
          panControl: false,
          mapTypeControl: false,
        }}
      >
        {groups.map((group) => {
          const first = group.events[0];
          const isMulti = group.events.length > 1;
          return (
            <Marker
              icon={markerIcon}
              title={
                isMulti
                  ? `${group.events.length} oportunidades de ${first.ongName}`
                  : first.titulo
              }
              position={{ lat: group.lat, lng: group.lng }}
              onClick={() => setSelectedKey(group.key)}
              key={group.key}
            >
              {selectedKey === group.key && (
                <InfoWindow
                  options={{ maxWidth: 400 }}
                  onCloseClick={() => setSelectedKey(null)}
                >
                  <div style={styles.container}>
                    <div style={styles.header}>
                      <div style={styles.ongName}>{first.ongName}</div>
                      {isMulti && (
                        <div style={styles.count}>
                          {group.events.length} oportunidades
                        </div>
                      )}
                    </div>
                    <div style={styles.list}>
                      {group.events.map((ev) => (
                        <div style={styles.item} key={ev.id}>
                          <div style={styles.itemTitulo}>{ev.titulo}</div>
                          {(!isMulti || group.events.length === 1) && ev.description && (
                            <div style={styles.itemDesc}>{ev.description}</div>
                          )}
                          {isMulti && ev.description && (
                            <div style={styles.itemDesc}>
                              {ev.description.length > 110
                                ? ev.description.slice(0, 110) + "…"
                                : ev.description}
                            </div>
                          )}
                          <button
                            type="button"
                            style={styles.button}
                            onClick={() => navigate(`/evento/${ev.id}`)}
                          >
                            Ver más
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          );
        })}
      </GoogleMap>
    );
  })


export default NormalMap;
